import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { getMainSliderReturnTo, mainSliderDetailState, MAIN_SLIDER_RETURN } from "@/lib/mainSliderNavigation";
import { publicApi } from "@/lib/api";
import { isLikelyHtml, resolveHtmlAssetUrls, sanitizeHtml } from "@/lib/html";
import type { HeroSlideDetail } from "@/lib/types";
import ScrollReveal from "@/components/base/ScrollReveal";

export default function MainSliderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getMainSliderReturnTo(location.state);
  const detailLinkState = returnTo ? mainSliderDetailState(returnTo) : mainSliderDetailState(MAIN_SLIDER_RETURN.home);

  const [detail, setDetail] = useState<HeroSlideDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await publicApi.get<HeroSlideDetail>(`/public/main-slider/${id}`);
      setDetail(res.data);
      setNotFound(false);
    } catch {
      setNotFound(true);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBack = () => {
    if (returnTo) navigate(returnTo);
    else if (window.history.length > 1) navigate(-1);
    else navigate(MAIN_SLIDER_RETURN.home);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-28 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1977cc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !detail) {
    return (
      <div className="min-h-screen bg-white pt-28 flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-50 text-gray-300">
          <i className="ri-slideshow-line text-2xl" />
        </div>
        <p className="text-gray-400 text-sm">슬라이드를 찾을 수 없습니다.</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-[#1977cc] text-white rounded-md text-sm font-medium hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
        >
          홈으로 돌아가기
        </button>
      </div>
    );
  }

  const { current, prev, next } = detail;

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-[#f1f7fc] pt-28 pb-10">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-6 bg-[#1977cc] rounded-full" />
              <h1 className="text-2xl md:text-3xl font-bold text-[#2c4964]">Main Slider</h1>
            </div>
            <nav className="text-sm text-gray-400 flex items-center gap-2 flex-wrap">
              <button
                onClick={handleBack}
                className="hover:text-[#1977cc] transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </button>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-[#2c4964] font-medium line-clamp-1">{current.title}</span>
            </nav>
          </ScrollReveal>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <div className="mb-6">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap mb-4 bg-violet-50 text-violet-700">
                Main Slider
              </span>
              <h2 className="text-xl md:text-2xl font-semibold text-[#2c4964] leading-snug mb-4">
                {current.title}
              </h2>
              <div className="flex items-center flex-wrap gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-calendar-line text-xs" />
                  {current.date}
                </span>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6 md:p-8">
              {current.content ? (
                isLikelyHtml(current.content) ? (
                  <div
                    className="rich-content text-gray-700 leading-relaxed text-[15px]"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(resolveHtmlAssetUrls(current.content)),
                    }}
                  />
                ) : (
                  <div className="text-gray-700 leading-relaxed text-[15px] whitespace-pre-line">
                    {current.content}
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-50 text-gray-300">
                    <i className="ri-slideshow-line text-xl" />
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed">등록된 본문이 없습니다.</p>
                </div>
              )}
            </div>

            {current.link?.trim() && (
              <div className="mt-6 flex items-center">
                <a
                  href={current.link}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1977cc]/8 text-[#1977cc] text-sm font-medium hover:bg-[#1977cc]/15 transition-colors"
                >
                  <i className="ri-external-link-line" />
                  원문 보기
                </a>
              </div>
            )}

            <div className="mt-10 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                <div className="py-5 sm:pr-6">
                  {prev ? (
                    <Link
                      to={`/main-slider/${prev.id}`}
                      state={detailLinkState}
                      className="group flex items-start gap-3 cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 group-hover:border-[#1977cc] group-hover:text-[#1977cc] group-hover:bg-[#1977cc]/5 transition-all shrink-0 mt-0.5">
                        <i className="ri-arrow-left-s-line text-base" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 mb-1 whitespace-nowrap">이전 슬라이드</p>
                        <p className="text-sm font-medium text-[#2c4964] group-hover:text-[#1977cc] transition-colors line-clamp-2 leading-snug">
                          {prev.title}
                        </p>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-300 shrink-0">
                        <i className="ri-arrow-left-s-line text-base" />
                      </div>
                      <p className="text-xs text-gray-300">이전 슬라이드가 없습니다</p>
                    </div>
                  )}
                </div>
                <div className="py-5 sm:pl-6 sm:text-right">
                  {next ? (
                    <Link
                      to={`/main-slider/${next.id}`}
                      state={detailLinkState}
                      className="group flex items-start gap-3 sm:flex-row-reverse cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 group-hover:border-[#1977cc] group-hover:text-[#1977cc] group-hover:bg-[#1977cc]/5 transition-all shrink-0 mt-0.5">
                        <i className="ri-arrow-right-s-line text-base" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 mb-1 whitespace-nowrap">다음 슬라이드</p>
                        <p className="text-sm font-medium text-[#2c4964] group-hover:text-[#1977cc] transition-colors line-clamp-2 leading-snug">
                          {next.title}
                        </p>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3 sm:flex-row-reverse">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-300 shrink-0">
                        <i className="ri-arrow-right-s-line text-base" />
                      </div>
                      <p className="text-xs text-gray-300">다음 슬라이드가 없습니다</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="pt-4 pb-2 flex justify-center">
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-gray-200 text-sm text-gray-500 hover:border-[#1977cc]/40 hover:text-[#1977cc] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-list-check text-xs" />
                  이전화면으로 돌아가기
                </button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
}
